/**
 * @description
 * Exposes APIs to to manager defer loaded components
 */

import { dispatchCaseViewRefresh } from '../refresh/refreshHelpers';

import type { DeferLoadComponentObj } from './types';

class DeferLoadManager {
  public components: {
    [contextName: string]: {
      [uniqueId: string]: DeferLoadComponentObj;
    };
  };

  constructor() {
    /**
     * {
     *  "<contextName>": {
     *    "uniquiId": {
              viewName,
              pageReference,
              contextName,
              isInProgress: true,
              active: true,
              caseKey,
      *    }
      *  }
      * }
      */
    this.components = {};
  }

  /**
   * Checks whether context is initialised or not
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   * @returns Returns boolean whether context is initialised or not
   */
  _checkComponent(uniqueId: string, contextName: string) {
    return !!this.components[contextName]?.[uniqueId];
  }

  /**
   * Initialize component cache with deferred component information
   * @param viewName The name of the view
   * @param caseKey case key used for refresh endpoint
   * @param pageReference Page reference
   * @param contextName Component context e.g. app/primary_1
   * @param uniqueId component identifier
   */
  start(viewName: string, caseKey: string, pageReference: string, contextName: string, uniqueId: string) {
    const component = {
      viewName,
      pageReference,
      contextName,
      isInProgress: true,
      active: true,
      caseKey
    };

    this.components[contextName] = this.components[contextName] || {};
    this.components[contextName][uniqueId] = component;
  }

  /**
   * Stops listening to the cascade event
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   */
  stop(uniqueId: string, contextName: string) {
    if (!this._checkComponent(uniqueId, contextName)) {
      return;
    }

    this.components[contextName][uniqueId].isInProgress = false;
  }

  /**
   * Removes component from the cache
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   */
  remove(uniqueId: string, contextName: string) {
    if (!this._checkComponent(uniqueId, contextName)) {
      return;
    }
    delete this.components[contextName][uniqueId];
  }

  /**
   * Removes component context from cache
   * @param contextName Component context e.g. app/primary_1
   */
  removeContext(contextName: string) {
    delete this.components[contextName];
  }

  /**
   * Mark component as cascade enable
   * @param contextName Component context e.g. app/primary_1
   */
  enableCascade(contextName: string) {
    if (!this.components[contextName]) {
      return;
    }
    Object.values(this.components[contextName]).forEach((component) => {
      if (component.isInProgress) {
        component.cascade = true;
      }
    });
  }

  /**
   * Activates the component
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   */
  activate(uniqueId: string, contextName: string) {
    if (!this._checkComponent(uniqueId, contextName)) {
      return;
    }
    this.components[contextName][uniqueId].active = true;
  }

  /**
   * Deactivates the component
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   */
  deactivate(uniqueId: string, contextName: string) {
    if (!this._checkComponent(uniqueId, contextName)) {
      return;
    }
    this.components[contextName][uniqueId].active = false;
  }

  /**
   * Refresh all active views associated to the component
   * @param contextName Component context e.g. app/primary_1
   */
  refreshActiveComponents(contextName: string) {
    if (!contextName) {
      return;
    }
    if (this.components[contextName]) {
      Object.keys(this.components[contextName]).forEach((uniqueId) => {
        this.refreshComponent(uniqueId, contextName);
      });
    }
  }

  /**
   * Refreshes view associated to the component
   * @param uniqueId component identifier
   * @param contextName Component context e.g. app/primary_1
   */
  refreshComponent(uniqueId: string, contextName: string) {
    if (!this._checkComponent(uniqueId, contextName)) {
      return;
    }
    const component = this.components[contextName][uniqueId];

    if (component.active && component.cascade) {
      const { caseKey, viewName, pageReference } = component;
      dispatchCaseViewRefresh(caseKey, viewName, contextName, pageReference, {});
    }
  }
}

export default new DeferLoadManager();
